/* Dependencies */
import anime from 'animejs';

// Models
import {
  SplitImageListFeatureItem,
  SplitImageListFeatureProps,
} from './SplitImageListFeature.model';

/**
 * Split Image List Feature
 */
class SplitImageListFeature {
  public parentElement: HTMLElement;
  public items: SplitImageListFeatureItem[];
  public activeItem: SplitImageListFeatureItem;

  constructor(props: SplitImageListFeatureProps) {
    this.parentElement = props.parentElement;
    this.items = props.items;

    // Set the default slide.
    this.showContent(props.currentDisplayIndex, true);

    // Attach events
    this.bindEvents();
  }

  /**
   * Show the desired content.
   * @param newIndex - The index to be rendered.
   * @param preventAnimation - Prevent the animation.
   */
  showContent(newIndex: number, preventAnimation?: boolean): void {
    const desiredItem = this.items[newIndex];

    // Ensure not the current active item.
    if (this.activeItem === desiredItem) return;

    // Hide the previous item
    if (this.activeItem) {
      this.hideContent(this.activeItem);
    }

    // Update State
    this.activeItem = desiredItem;

    // Update the aria state
    desiredItem.triggerElement.setAttribute('aria-expanded', 'true');

    // Apply styles without animation
    if (preventAnimation) {
      desiredItem.contentElements.forEach((element) => {
        element.classList.add('tw-block');
        element.classList.remove('tw-hidden');
        element.setAttribute('data-active', 'true');
      });
      return;
    }

    // Animate in the new element
    anime({
      begin: () => {
        desiredItem.contentElements.forEach((element) => {
          element.classList.add('tw-block');
          element.classList.remove('tw-hidden');
          element.setAttribute('data-active', 'true');

          element.classList.remove('tw-hidden');
          element.classList.add('tw-opacity-0');
          element.classList.add('tw-block');
        });
      },
      targets: desiredItem.contentElements,
      opacity: [0, 1],
      duration: 200,
      easing: 'linear',
    });
  }

  /**
   * Hide the row
   */
  hideContent(activeItem: SplitImageListFeatureItem): void {
    // Update the state
    activeItem.contentElements.forEach((element) => {
      element.setAttribute('data-active', 'false');

      // Remove the content element
      element.classList.remove('tw-block');
      element.classList.add('tw-hidden');
    });

    // Update the aria state
    activeItem.triggerElement.setAttribute('aria-expanded', 'false');
  }

  /**
   * Binds the related events for the section.
   */
  bindEvents(): void {
    this.items.forEach((item, index) => {
      item.triggerElement.addEventListener('click', (event) => {
        this.showContent(index);
      });

      item.triggerElement.addEventListener('mouseenter', (event) => {
        this.showContent(index);
      });
    });
  }
}

export const splitImageListFeatureInit = () => {
  const splitImageListFeatureElements: HTMLElement[] = Array.from(
    document.querySelectorAll('[data-split-image-list-feature]')
  );

  splitImageListFeatureElements.forEach((containerElement) => {
    const listItems: HTMLElement[] = Array.from(
      containerElement.querySelectorAll('[data-item]')
    );

    // Config
    const currentDisplayIndex = containerElement.getAttribute(
      ' data-default-index'
    )
      ? +containerElement.getAttribute('data-default-index')
      : 0;

    const contentItems = listItems.map((listItem, index) => {
      // Content Elements
      const contentElements: HTMLElement[] = Array.from(
        containerElement.querySelectorAll(`[data-content="${index + 1}"]`)
      );
      const triggerElement: HTMLElement =
        listItem.querySelector('[data-trigger]');

      return {
        triggerElement,
        contentElements,
      };
    });

    new SplitImageListFeature({
      parentElement: containerElement,
      items: contentItems,
      currentDisplayIndex,
    });
  });
};
